import { efficiencyOf, marketDisplayName, MarketSummary } from '../utils/dtc-commerce-mmm-markets';
import {
  MarketSortColumn,
  MarketSortColumnType,
  MarketTableRow,
  SortDirection,
  SortDirectionType
} from './market-performance-table-constants';

export const buildMarketTableRows = (
  markets: MarketSummary[],
  isRevenue: boolean,
  dmaNames?: Record<string, string>
): MarketTableRow[] =>
  markets.map(m => ({
    ...m,
    displayName: marketDisplayName(m.geo, dmaNames),
    efficiency: efficiencyOf(m.spend, m.contribution, isRevenue)
  }));

const sortValue = (
  row: MarketTableRow,
  column: MarketSortColumnType,
  isRevenue: boolean,
  missing: number
): number => {
  switch (column) {
    case MarketSortColumn.SPEND_PCT:
      return row.spendShare;
    case MarketSortColumn.EFFECT:
      return row.contribution;
    case MarketSortColumn.EFFECT_PCT:
      return row.contributionShare;
    case MarketSortColumn.EFFICIENCY:
      // CPA is better when lower, so "high to low" lists the cheapest first, as in
      // Channel Performance.
      if (row.efficiency === null) return missing;
      return isRevenue ? row.efficiency : -row.efficiency;
    case MarketSortColumn.SPEND:
    default:
      return row.spend;
  }
};

/**
 * Named markets in the chosen order, then the pooled market whatever the sort, so the rows above
 * the total read as one list of places and the last row before the total is everything else. A
 * market with no ROAS or CPA sorts last either way.
 */
export const sortMarketRows = (
  rows: MarketTableRow[],
  column: MarketSortColumnType,
  direction: SortDirectionType,
  isRevenue: boolean
): MarketTableRow[] => {
  const sign = direction === SortDirection.ASC ? 1 : -1;
  const missing = direction === SortDirection.ASC ? Infinity : -Infinity;
  const compare = (a: MarketTableRow, b: MarketTableRow): number => {
    if (column === MarketSortColumn.MARKET) {
      return a.displayName.localeCompare(b.displayName) * sign;
    }
    const aValue = sortValue(a, column, isRevenue, missing);
    const bValue = sortValue(b, column, isRevenue, missing);
    return aValue === bValue ? 0 : (aValue - bValue) * sign;
  };
  const named = rows.filter(r => !r.isPooled).sort(compare);
  return [...named, ...rows.filter(r => r.isPooled)];
};
